import { Cookie } from '@atlassiansox/asc-core';
import { Asap, UserProfile } from './models';
import { PRODUCT_KEY_MAP } from './constants';
import { XOR } from './utils';

type ProductActivation = {
  readonly productKey: string;
  readonly edition?: string;
};

export type GetUserPermissionsIsPermittedRequest = {
  readonly permissionId: GetUserPermissionsIsPermittedRequestPermissionId;
  readonly resourceId: string;
  readonly dontRequirePrincipalInSite?: boolean;
  readonly webPlatform?: string;
};

export type GetUserPermissionsIsPermittedResponse = {
  readonly permitted: boolean;
};

export enum AuthenticationMethodsType {
  PASSWORD = 'password',
  GOOGLE = 'google',
  SAML = 'saml',
  MICROSOFT = 'microsoft',
  APPLE = 'apple',
  SLACK = 'slack'
}

type Method = {
  readonly type: AuthenticationMethodsType;
  readonly redirect_to?: string;
};

export type CheckAuthenticationMethodsAvailableResponse = {
  readonly methods: Method[];
  readonly userExists: boolean;
  readonly passwordEnabled: boolean;
  readonly samlRequired: boolean;
};

export type PostCloudSignUpRequest = {
  payload: FormPayloadType | FormPayloadActivateProducts;
  webPlatform?: string;
};

type ProductPayloadObject = {
  readonly accountName?: string | null;
  readonly edition?: string | null;
  readonly product?: string | null;
  readonly productKey?: string | null;
};

type FormPayloadActivateProducts = {
  activateProducts?: boolean;
  products: ProductPayloadObject[];
  cloudId?: string;
  cloudName: string | null;
  advancedSettings?: {
    [key: string]: number | boolean | string | Record<string, unknown>;
  };
  signupContext?: string;
};

type ConsentsObject = {
  key: string;
  displayedText: unknown;
  granted: boolean;
};

export type Consent = {
  readonly site: string | null;
  readonly locale: string | null;
  readonly formUrl: string | null;
  readonly consents: ConsentsObject[];
};

type FormPayloadType = {
  adminUsername: string;
  gRecaptchaResponse?: string;
  cloudName: string | null;
  email: string | null;
  timezone: string | null;
  firstName: string | null;
  lastName: string | null;
  adminPassword?: string | null;
  signupContext?: string;
  dataRegion?: string;
  consent: Consent;
} & FormPayloadActivateProducts;

export type CheckAuthenticationMethodsAvailableRequest = {
  readonly email: string;
  readonly application?: string;
  readonly webPlatform?: string;
  readonly successRedirect: string;
};

export enum GetUserPermissionsIsPermittedRequestPermissionId {
  ADD_PRODUCTS = 'add-products',
  INVITE_USERS = 'invite-users',
  MANAGE = 'manage',
  WRITE = 'write',
  /**
   * @deprecated ADD_PRODUCT is a legacy permissionId, use ADD_PRODUCTS instead.
   */
  ADD_PRODUCT = 'addProduct'
}

export type RenameCloudRequest = {
  readonly webPlatform?: string;
  readonly cloudId: string;
  readonly cloudName: string;
};

export type RenameCloudResponse = {
  readonly progressUri: string;
};

export type CheckCloudNameAvailabilityRequest = {
  readonly cloudName: string;
  readonly webPlatform?: string;
};

export type CheckCloudNameAvailabilityResponse = {
  readonly availability: CloudNameAvailability;
};

export type GetUserCloudSitesRequest = {
  readonly product: string;
  readonly editions: string[];
  readonly adminAccess: boolean;
  readonly billingFrequency: string[];
  readonly licenseLevels: string[];
};

export type GetUserCloudSitesResponse = {
  readonly name: string;
  readonly sites: ReadonlyArray<CloudSitesInfo>;
};

type CloudSitesInfo = {
  readonly siteName: string;
  readonly edition: string;
  readonly license: string;
  readonly product: string;
  readonly siteId: string;
  readonly adminAccess: boolean;
};

export type CloudNameAvailability =
  | 'AVAILABLE'
  | 'TAKEN'
  | 'ERROR'
  | 'INVALID'
  | 'NOT_AVAILABLE_FOR_TRANSFER';

export type SitesType = {
  readonly adminAccess: boolean;
  readonly cloudId: string;
  readonly products: string[];
  readonly url: string;
  readonly displayName: string;
  readonly edition?: string | null;
  readonly orgId?: string | null;
  readonly productsAndEditions?: ProductsAndEditionsObjectType[];
};

export type CloudSessionTokenCookieType = {
  readonly cloudSessionTokenCookie?: Cookie;
};

export type AvailableProductsResponse = {
  sites: SitesType[];
  isNewUser: boolean;
};

export type AvailableProductsLegacyResponse = SitesType[];

export type AvailableProductsOptions = {
  readonly includeInvalidSites?: boolean;
  readonly includeInvalidBitbucketSite?: boolean;
  readonly removeNonAdminSites?: boolean;
} & CloudSessionTokenCookieType;

export type ProductType = {
  productKey?: string;
  edition?: string;
};

export type UserSignupRequest = {
  email: string;
  locale: string;
  redirectTo: string;
  displayName: string;
  gRecaptchaResponse?: string;
  webPlatform?: string;
  products?: ProductType[];
};

export type UserSignupResponse = {
  id: string;
  otpRedirect?: string;
};

type ActivateProductResponseItems = {
  itemId: string;
  orderItemId: string;
};

export type Platform = 'ccp' | 'hams' | 'routing';

export type CcpProduct = {
  readonly offeringId: string;
  readonly pricingPlanId: string;
  readonly productKey: string;
  readonly edition: string;
  readonly chargeElement: string;
  readonly pricingType?: string;
};

export type ActivateProductRequest = {
  email: string;
  ccpProducts: CcpProduct[];
  siteName?: string;
  timezone: string;
  useCcpPlatform: boolean;
  platform: Platform;
  displayName?: string;
  consent?: Consent;
  signupContext?: string;
  advancedSettings?: {
    [key: string]: number | boolean | string | Record<string, unknown>;
  };
  gRecaptchaResponse?: string;
  testingPurposes?: boolean;
  isNewUser?: boolean;
  migrationId?: string;
  webPlatform?: string;
  orgId?: string;
  workspaceId?: string;
};

export type ActivateProductUpstreamResponse = XOR<
  ActivateProductCcpUpstreamResponse,
  ActivateProductCofsResponse
>;

export type ActivateProductCcpUpstreamResponse = {
  statusUrl: string;
  orderId: string;
  slug: string;
  items: ActivateProductResponseItems[];
  transactionAccountId: string;
  invoiceGroupId: string;
  metadata: {
    provisionRequestId: string;
  };
  cloudId: string;
  service: string;
};

export type ActivateProductResponse = XOR<
  ActivateProductCcpResponse,
  ActivateProductCofsResponse
>;

export type ActivateProductCcpResponse = {
  orderId: string;
  slug: string;
  transactionAccountId: string;
  invoiceGroupId: string;
  metadata: {
    provisionRequestId: string;
  };
  items: ActivateProductResponseItems[];
  cloudId: string;
  service: string;
};

export type ActivateProductCofsResponse = {
  cloudId: string;
  progressUri: string;
  service: string;
};
export type CofsActivateProductExistingRequest = {
  cloudId: string;
  products: ProductActivation[];
  timezone: string;
  signupContext?: string;
  webPlatform?: string;
  advancedSettings?: {
    [key: string]: number | boolean | string | Record<string, unknown>;
  };
  gRecaptchaResponse?: string;
};
export interface ProductActivationStatusRequest {
  provisioningRequestId: string;
  webPlatform?: string;
}

export interface ProductActivationStatusResponse {
  provisionRequestId: string;
  resourcePackages: ResourcePackage[];
}

export interface GetLicenceInformationRequest {
  cloudId: string;
  webPlatform?: string;
}
export type GetLicenseInformationResponse = {
  readonly firstActivationDate: number;
  readonly hostname: string;
  readonly products: {
    [key: string]: GetLicenseInformationProduct;
  };
};

export type GetLicenseInformationProduct = {
  readonly ccpEntitlementId?: string;
  readonly ccpTransactionAccountId: string;
  readonly edition: string;
  readonly isSuspended?: boolean;
  readonly state: string;
  readonly relatesFromEntitlements?: GetLicenseInformationRelatedEntitlement[];
};

export type GetLicenseInformationRelatedEntitlement = {
  readonly relationshipType: string;
  readonly entitlementId: string;
  readonly relationshipId: string;
};

export interface ProductActivateRequest {
  readonly transactionAccountId: string;
  readonly entitlementId: string;
  readonly offerings: OfferingsType[];
  readonly cloudId: string;
  readonly siteName?: string;
  readonly webPlatform?: string;
  readonly advancedSettings?: advancedSettingsType;
}

// For cross flow /product/activate endpoint
export type advancedSettingsType = {
  readonly signupContext?: string;
};

export interface COFSProductActivateRequest {
  readonly edition: string;
  readonly productKey: string;
  readonly cloudId: string;
  readonly webPlatform?: string;
}

export type OfferingsType = {
  readonly offeringId: string;
  readonly pricingPlanId: string;
  readonly chargeElement: string;
};

export type ProductActivateResponse = {
  readonly orderId: string;
  readonly progressUri: string;
  readonly cloudId: string;
};
export type COFSProductActivateResponse = {
  readonly progressUri: string;
  readonly cloudId: string;
};

export interface ResourcePackage {
  entitlementId: string;
  action: {
    type: RecourcePackageActionTypes;
    status: ProductActivationStatus;
    errorDetails?: ResourceActivationError[];
  };
  resourceAris: string[];
}

export type RecourcePackageActionTypes = 'ACTIVATE';

export type ProductActivationStatus =
  | 'IN_PROGRESS'
  | 'SUCCESS'
  | 'PROVISIONING_FAILURE';

export interface ResourceActivationError {
  source: string;
  message: string;
}

/**
 * Identity Client section
 */
export type AsapAuth = {
  readonly asap: Asap;
};

export function isAsapAuth(auth: AsapAuth) {
  return !!auth.asap;
}

export type MeResponse = {
  readonly account_id: string;
  readonly name: string;
  readonly email: string;
  readonly picture: string;
  readonly locale: string;
  readonly zoneinfo: string;
};

export type MeOptions = CloudSessionTokenCookieType;

export function mapMeResponseToUserProfile({
  account_id,
  ...others
}: MeResponse): UserProfile {
  return {
    accountId: account_id,
    ...others
  };
}

export type BitbucketWorkspaceOptions = {
  readonly nextUrl?: string;
  readonly urlQuery?: string;
} & CloudSessionTokenCookieType;

export type BitbucketWorkspacesResponse = {
  readonly page: number;
  readonly pagelen: number;
  readonly size: number;
  readonly next?: string;
  readonly previous?: string;
  readonly values: [
    {
      // owner: has admin access, collaborator: has write access
      readonly permission: 'owner' | 'member' | 'collaborator';
      readonly user?: WorkspaceUserType;
      readonly workspace?: WorkspaceType;
      readonly last_accessed?: string | null;
      readonly type?: string;
      readonly links?: Record<string, unknown>;
      readonly added_on?: string | null;
    }
  ];
};

export type WorkspaceUserType = {
  readonly display_name: string;
  readonly account_id: string;
  readonly links: Record<string, unknown>;
  readonly nickname: string;
  readonly type: string;
  readonly uuid: string;
};

export type WorkspaceType = {
  readonly name: string;
  readonly type: string;
  readonly uuid: string;
  readonly links: Record<string, unknown>;
  readonly slug: string;
};

export type AvailableProductsType = {
  readonly adminAccess: boolean;
  readonly cloudId: string;
  readonly displayName: string;
  readonly url: string;
  readonly availableProducts: [
    {
      readonly activityCount: number;
      readonly productType: keyof typeof PRODUCT_KEY_MAP;
      readonly url?: string;
    }
  ];
  readonly avatar?: string;
};

/**
 * /user/:id/manage/profile
 */

export type PatchUserProfileRequestBody = {
  readonly name?: string;
  readonly nickname: string;
};

export type PatchUserProfileRequest = {
  readonly cloudSessionTokenCookie: Cookie;
  readonly body: PatchUserProfileRequestBody;
  readonly userId: string;
};

export type PermissionsPermittedRequestAsap = {
  readonly auth: AsapAuth;
};
export type PermissionsPermittedRequestParams = {
  readonly dontRequirePrincipalInSite: boolean;
  readonly permissionId: string;
  readonly principalId: string;
  readonly resourceId: string;
};
export type GetCcpHamsStatus = {
  readonly cloudId: string;
  readonly offeringKeys: string;
};
export type GetCcpHamsResponse = {
  readonly service: string;
};

export type GetAvailableSitesRequest = {
  readonly orderId: string;
  readonly transactionAccount: string;
  readonly webPlatform?: string;
};
type ProductsAndEditionsObjectType = {
  readonly productEdition: string;
  readonly productKey: string;
};
export type Site = {
  readonly siteName: string;
  readonly cloudId: string;
  readonly products: string[];
  readonly displayName: string;
  readonly url: string;
  readonly edition: string;
  readonly productsAndEditions?: ProductsAndEditionsObjectType[];
  readonly entitlements: {
    ccpEntitlementId: string;
    billingSourceSystem: string;
    productKey: string;
  }[];
  readonly adminAccess: boolean;
};

export type SiteFlags = {
  readonly useLockedInSite: boolean;
};

export type GetAvailableSitesResponse = {
  readonly sites: Site[];
  readonly flags: SiteFlags;
};

export type PermissionsPermittedRequest = PermissionsPermittedRequestAsap &
  PermissionsPermittedRequestParams;

export type PermissionsPermittedResponse = string;

/**
 * getProductHubInfo section
 * TODO: find a single source for these models (these were just copied over from bxp-express)
 */

export enum Editions {
  FREE = 'free',
  STANDARD = 'standard',
  PREMIUM = 'premium'
}

export enum ProductKeys {
  CONFLUENCE = 'confluence.ondemand',
  JIRA_CORE = 'jira-core.ondemand',
  JIRA_SERVICE_DESK = 'jira-servicedesk.ondemand',
  JIRA_SOFTWARE = 'jira-software.ondemand',
  JIRA_PRODUCT_DISCOVERY = 'jira-product-discovery',
  TRELLO = 'trello',
  OPSGENIE = 'opsgenie',
  BITBUCKET = 'bitbucket.ondemand',
  BITBUCKET_COFS = 'com.atlassian.bitbucket',
  STATUSPAGE = 'statuspage',
  TEAM_CENTRAL = 'townsquare',
  AVOCADO = 'avocado',
  COMPASS = 'compass',
  ANALYTICS = 'atlassian-analytics-free',
  WHITEBOARD = 'atlassian-whiteboard',
  GUARD = 'guard',
  ROVO = 'rovo'
}

export interface RelatesFromEntitlements {
  entitlementId: string;
  relationshipId: string;
  relationshipType: string;
}

export interface Product {
  applicationUrl: string;
  edition: Editions;
  isSuspended: boolean;
  state: 'ACTIVE' | 'DEACTIVATED' | string;
  adminAccess?: boolean;
  billingPeriod?: 'MONTHLY' | 'ANNUAL';
  ccpTransactionAccountId?: string;
  ccpEntitlementId?: string;
  relatesFromEntitlements?: RelatesFromEntitlements[];
  trialEndDate?: string;
  lastActiveTimestamp: string | null;
}

export interface ProductHub {
  cloudId: string;
  hostname: string;
  firstActivationDate: number;
  products?: Partial<{
    [key in ProductKeys]: Product;
  }>;
  adminAccess: boolean;
}

export type GetProductHubInfoResponse = {
  readonly sites: ProductHub[];
};

export interface ActivateDirectBuyProductRequest {
  timezone: string;
  region: string;
  orderId: string;
  transactionAccountId: string;
  webPlatform?: string;
  site: XOR<
    ActivateDirectBuyProductForExistingSiteRequest,
    ActivateDirectBuyProductForNewSiteRequest
  >;
}

export interface ActivateDirectBuyProductForExistingSiteRequest {
  cloudId: string;
}

export interface ActivateDirectBuyProductForNewSiteRequest {
  name: string;
}

export interface ActivateDirectBuyProductResponse {
  provisionRequestId: string;
}

export interface GetCloudProductInfoRequest {
  cloudId: string;
  webPlatform?: string;
}

export type CloudProductInfo = Record<string, string>;

export type GetProductEditionRequest = {
  readonly cloudId: string;
  readonly productKey: string;
};

export type GetProductEditionResponse = {
  readonly edition: string;
};

export type getConsentConfigResponse = {
  readonly locale: string;
  readonly localeRequiresMarketingCommunicationOptIn: boolean;
  readonly hasUserMadeChoiceOnMarketingCommunicationOptIn: boolean;
  readonly usersChoiceOnMarketingCommunicationOptIn: boolean;
};

export type postConsentConfigResponse = {
  readonly locale: string;
  readonly localeRequiresMarketingCommunicationOptIn: boolean;
  readonly hasUserMadeChoiceOnMarketingCommunicationOptIn: boolean;
  readonly usersChoiceOnMarketingCommunicationOptIn: boolean;
};

export type PostExpandFreeValidationRequest = {
  readonly cloudId: string;
  readonly productKeys: string[];
};

export type PostExpandFreeValidationResponse = {
  readonly isInBreachOfFree: boolean;
};

export type GetActivationAuthorizationRequest = {
  readonly productKeys: string[];
  readonly productEdition: string;
  readonly cloudId?: string;
  readonly webPlatform?: string;
  readonly siteName: string;
};

export type GetActivationAuthorizationResponse = {
  readonly redirectUrl: string;
  readonly authorized: boolean;
};

export type GetFeatureFlagRequest = string;

export type GetFeatureFlagResponse = {
  readonly result: unknown;
};

export type GetFeatureFlagSyncRequest = string;

export type GetFeatureFlagSyncResponse = {
  readonly result: unknown;
};

export type GetRecommendedSiteNameRequest = {
  readonly email: string;
  readonly webPlatform?: string;
} & CloudSessionTokenCookieType;

export type GetRecommendedSiteNameResponse = {
  readonly recommendedName: string;
};

export type GetAccountOrgsRequest = {
  readonly includeVortexMode?: boolean;
  readonly webPlatform?: string;
};

export type GetAccountOrgsResponse = {
  readonly id: string;
  readonly name: string;
  readonly isAdmin: boolean;
  readonly vortexMode?: string;
}[];

export type CreateOrgRequest = {
  readonly orgDisplayName: string;
  readonly webPlatform?: string;
};

export type CreateOrgResponse = {
  readonly orgId: string;
  readonly progressUri: string;
};

export type BitbucketValidateWorkspaceSlugRequest = {
  readonly workspaceSlug: string;
};

export type BitbucketValidateWorkspaceSlugResponse = {
  readonly new_ari: string;
};
